// SurveyConnectivityContactForm.tsx
import React, { FunctionComponent } from 'react';
import { Button, Card, Form, Input, Tooltip } from 'antd';
import { SaveOutlined, UsergroupAddOutlined, UsergroupDeleteOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';

import { SurveyContactInput } from '../../../../__generated__/globalTypes';

interface SurveyConnectivityContactFormProps {
    form: any;
    contactInfo?: SurveyContactInput[];
    onFinish: (values: any) => void;
    onDeleteSecondContact: () => void;
    isReadOnly: boolean;
}

const SurveyConnectivityContactForm: FunctionComponent<SurveyConnectivityContactFormProps> = ({
    form,
    contactInfo,
    onFinish,
    onDeleteSecondContact,
    isReadOnly,
}) => {
    const intl = useIntl();

    return (
        <Form
            form={form}
            layout="horizontal"
            onFinish={onFinish}
            initialValues={{ items: contactInfo || [{}] }}
            noValidate={true}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign={'left'}
        >
            <Form.List name="items">
                {(fields, { add, remove }) => (
                    <div style={{ display: 'flex', rowGap: 16, flexDirection: 'column' }}>
                        {fields.map((field) => (
                            <Card
                                size="small"
                                title={intl.formatMessage(
                                    {
                                        id: 'survey-connectivityInfo-technicalContactDetails-card-title',
                                        defaultMessage: 'Technical Contact {contactNumber}',
                                    },
                                    { contactNumber: Number(field.name) + 1 }
                                )}
                                key={field.key}
                            >
                                <Form.Item
                                    label={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContactDetails-contactName',
                                        defaultMessage: 'Technical Contact Name',
                                    })}
                                    name={[field.name, 'contactFirstName']}
                                    rules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'generic-requiredField-message',
                                                defaultMessage: 'Please Enter Required Data',
                                            }),
                                        },
                                    ]}
                                >
                                    <Input className="mr10" type={'text'} readOnly={isReadOnly} />
                                </Form.Item>
                                <Form.Item
                                    label={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContactDetails-contactSurname',
                                        defaultMessage: 'Technical Contact Surname',
                                    })}
                                    name={[field.name, 'contactLastName']}
                                    rules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'generic-requiredField-message',
                                                defaultMessage: 'Please Enter Required Data',
                                            }),
                                        },
                                    ]}
                                >
                                    <Input className="mr10" type={'text'} readOnly={isReadOnly} />
                                </Form.Item>
                                <Form.Item
                                    label={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContactDetails-contactNumber',
                                        defaultMessage: 'Technical Contact Number',
                                    })}
                                    name={[field.name, 'contactNumber']}
                                    rules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'generic-requiredField-message',
                                                defaultMessage: 'Please Enter Required Data',
                                            }),
                                        },
                                    ]}
                                >
                                    <Input className="mr10" type={'tel'} readOnly={isReadOnly} />
                                </Form.Item>
                                <Form.Item
                                    label={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContactDetails-contactEmail',
                                        defaultMessage: 'Technical Contact Email Address',
                                    })}
                                    name={[field.name, 'contactEmailAddres']}
                                    rules={[
                                        {
                                            required: true,
                                            message: intl.formatMessage({
                                                id: 'generic-requiredField-message',
                                                defaultMessage: 'Please Enter Required Data',
                                            }),
                                        },
                                        {
                                            type: 'email',
                                            message: intl.formatMessage({
                                                id: 'survey-connectivityInfo-invalid-email',
                                                defaultMessage:
                                                    "Email must include one '@' symbol, a domain, no spaces, and only valid characters.",
                                            }),
                                        },
                                    ]}
                                >
                                    <Input className="mr10" type={'email'} readOnly={isReadOnly} />
                                </Form.Item>
                                <Form.Item
                                    label={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContactDetails-date',
                                        defaultMessage: 'Date',
                                    })}
                                    name={[field.name, 'updateTime']}
                                >
                                    <Input className="mr10" type={'text'} readOnly={true} />
                                </Form.Item>
                            </Card>
                        ))}

                        <div>
                            {fields.length === 1 && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContact-add-second',
                                    })}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            add();

                                        }}
                                        size="large"
                                        icon={<UsergroupAddOutlined />}
                                    />
                                </Tooltip>
                            )}

                            {fields.length === 2 && (
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: 'survey-connectivityInfo-technicalContact-remove-second',
                                        defaultMessage: 'Remove second additional contact"',
                                    })}
                                >
                                    <Button
                                        style={{ marginLeft: '10px'}}
                                        type="primary"
                                        onClick={() => {
                                            remove(1);
                                            onDeleteSecondContact();
                                        }}
                                        size="large"
                                        icon={<UsergroupDeleteOutlined />}
                                    />
                                </Tooltip>
                            )}

                            <Tooltip
                                title={intl.formatMessage({ id: 'survey-connectivityInfo-technicalContact-save' })}
                            >
                                <Button
                                    type="primary"
                                    size="large"
                                    style={{ marginLeft: '10px' }}
                                    hidden={isReadOnly}
                                    icon={<SaveOutlined />}
                                    htmlType="submit"
                                />
                            </Tooltip>
                        </div>
                    </div>
                )}
            </Form.List>
        </Form>
    );
};

export default SurveyConnectivityContactForm;
